import { createRouter, createWebHistory } from 'vue-router'
import { trackRouter } from 'vue-gtag-next'
import store from '@/store'
import { ENTER_SITE } from '@/store/action-types'
import { SET_USER_DATA, SET_ADV_MSGS } from '@/store/mutation-types'

const Dashboard = () => import('@/views/Dashboard/Dashboard.vue')
const Register = () => import('@/views/Register/Register.vue')
const EditProfile = () => import('@/views/EditProfile/EditProfile.vue')

const ManageAccounts = () => import('@/views/ManageAccounts/ManageAccounts.vue')
const ManageSubmissions = () =>
  import('@/views/ManageSubmissions/ManageSubmissions.vue')
const ManageLetterhead = () =>
  import('@/views/ManageLetterhead/ManageLetterhead.vue')

const AdminDashboard = () => import('@/views/AdminDashboard/AdminDashboard.vue')
const AdvisoryMessages = () =>
  import('@/views/AdminDashboard/AdvisoryMessages.vue')
const ReleaseNotes = () => import('@/views/AdminDashboard/ReleaseNotes.vue')
const ESPStatistics = () => import('@/views/AdminDashboard/ESPStatistics.vue')

const NewClaim = () => import('@/views/NewClaim/NewClaim.vue')
const Markets = () => import('@/views/NewClaim/Markets.vue')
const Events = () => import('@/views/NewClaim/Events.vue')
const Upload = () => import('@/views/NewClaim/Upload.vue')
const Review = () => import('@/views/NewClaim/Review.vue')
const Confirm = () => import('@/views/NewClaim/Confirm.vue')

const SubmitVoucher = () => import('@/views/SubmitVoucher/SubmitVoucher.vue')
const UploadVoucher = () => import('@/views/SubmitVoucher/UploadVoucher.vue')
const ReviewVoucher = () => import('@/views/SubmitVoucher/ReviewVoucher.vue')
const ConfirmVoucher = () => import('@/views/SubmitVoucher/ConfirmVoucher.vue')
const ManageVouchers = () => import('@/views/ManageVouchers/ManageVouchers.vue')

const ClaimSandbox = () => import('@/views/ClaimSandbox/ClaimSandbox.vue')
const Reports = () => import('@/views/Reports/Reports.vue')
const ClaimsReport = () => import('@/views/Reports/ClaimsReport.vue')
const PaymentsReport = () => import('@/views/Reports/PaymentsReport.vue')
const EventsReport = () => import('@/views/Reports/EventsReport.vue')
const ScheduledReports = () => import('@/views/Reports/ScheduledReports.vue')

const ErrorPage = () => import('@/views/ErrorPage/ErrorPage.vue')
const SiteMaintenance = () =>
  import('@/views/SiteMaintenance/SiteMaintenance.vue')
const ReferenceGuide = () => import('@/views/ReferenceGuide/ReferenceGuide.vue')

const DEEPLINK_SESSION_STORAGE_KEY = `gts-esp-deeplink__${__ENV}_v${__APP_VERSION}`

/**
 * Checks whether or not the user can access a task
 * @param {any} to
 * @returns Object or null (falsy check)
 */
const hasRights = function (to) {
  const _tasks = store.state.user.current.tasks

  let _allTasks = []
  for (const _task in _tasks) {
    if (Object.hasOwnProperty.call(_tasks, _task)) {
      _allTasks = _allTasks.concat(_tasks[_task])
    }
  }

  let _routeName = to.name.split('-')[0]

  let _hasRights = _allTasks.find((el) => el.name === _routeName)

  return !!_hasRights
}

/**
 * Redirects user to Unauthorized page
 * @param {function} next
 */
const kickThemOut = function (next) {
  return next('/error/unauthorized')
}

/**
 * Prevents access to a route based on 'permissions' to tasks.
 * If 'to' route does not match the allowed set of tasks aka Features,
 * user will be redirected to 403 page, otherwise allowed to proceed
 * @param {any} to
 * @param {any} from
 * @param {any} next
 */
const checkPermissions = function (to, from, next) {
  if (hasRights(to)) {
    next()
  } else {
    kickThemOut(next)
  }
}

const SITE_MAINTENANCE_URL = 'site-maintenance'

const routes = [
  {
    path: '/',
    name: 'home',
    component: Dashboard,
    meta: {
      auth: true,
    },
    beforeEnter(to, from, next) {
      // If the user does not have any of the main features but has Claim Sandbox,
      // forward them to Claim Sandbox route
      let _tasks = store.state.user.current.tasks
      let shouldGoToClaimSandbox = !_tasks.Features && _tasks.ClaimSandbox
      if (shouldGoToClaimSandbox) {
        let _route = store.state.user.current.tasks.ClaimSandbox[0].route
        next(_route)
      } else next()
    },
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: {
      auth: false,
    },
    beforeEnter(to, from, next) {
      if (store.state.user.isAuthenticated) next('/')
      else next()
    },
  },
  {
    path: '/edit-profile',
    name: 'editProfile',
    component: EditProfile,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/manage-accounts',
    name: 'manageAccounts',
    component: ManageAccounts,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/manage-letterhead',
    name: 'manageLetterhead',
    component: ManageLetterhead,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/admin',
    name: 'adminDashboard',
    component: AdminDashboard,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/admin/advisory-messages',
    name: 'advisoryMessages',
    component: AdvisoryMessages,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/admin/release-notes',
    name: 'releaseNotes',
    component: ReleaseNotes,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/admin/stats',
    name: 'espStatistics',
    component: ESPStatistics,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/new-claim',
    component: NewClaim,
    meta: {
      auth: true,
    },
    children: [
      {
        path: '',
        name: 'fileClaim',
        redirect: '/new-claim/markets',
      },
      {
        path: 'markets',
        name: 'fileClaim-markets',
        component: Markets,
      },
      {
        path: 'events',
        name: 'fileClaim-events',
        component: Events,
      },
      {
        path: 'upload',
        name: 'fileClaim-upload',
        component: Upload,
      },
      {
        path: 'review',
        name: 'fileClaim-review',
        component: Review,
      },
      {
        path: 'confirm',
        name: 'fileClaim-confirm',
        component: Confirm,
      },
    ],
    beforeEnter: checkPermissions,
  },
  {
    path: '/manage-submissions/:coi?',
    name: 'manageSubmissions',
    component: ManageSubmissions,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/submit-voucher',
    component: SubmitVoucher,
    meta: {
      auth: true,
    },
    children: [
      {
        path: '',
        name: 'submitVoucher',
        redirect: '/submit-voucher/upload',
      },
      {
        path: 'upload',
        name: 'submitVoucher-upload',
        component: UploadVoucher,
      },
      {
        path: 'review',
        name: 'submitVoucher-review',
        component: ReviewVoucher,
      },
      {
        path: 'confirm',
        name: 'submitVoucher-confirm',
        component: ConfirmVoucher,
      },
    ],
    beforeEnter: checkPermissions,
  },
  {
    path: '/manage-vouchers/:coi?',
    name: 'manageVouchers',
    component: ManageVouchers,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/reports',
    name: 'viewReports',
    component: Reports,
    meta: {
      auth: true,
    },
    children: [
      {
        path: 'claims',
        name: 'viewReports-claims',
        component: ClaimsReport,
      },
      {
        path: 'payments',
        name: 'viewReports-payments',
        component: PaymentsReport,
      },
      {
        path: 'events',
        name: 'viewReports-events',
        component: EventsReport,
      },
      {
        path: 'scheduled',
        name: 'viewReports-scheduled',
        component: ScheduledReports,
      },
    ],
    beforeEnter(to, from, next) {
      if (hasRights(to)) {
        const reportsDashboard = '/reports'
        const hasSubRoute = to.matched.length > 1
        const originNotReportsDashboard = from.path !== reportsDashboard
        const destinationNotScheduledReports =
          to.path !== reportsDashboard + '/scheduled'

        if (
          hasSubRoute &&
          originNotReportsDashboard &&
          destinationNotScheduledReports
        ) {
          next(reportsDashboard)
        } else {
          next()
        }
      } else {
        kickThemOut(next)
      }
    },
  },
  {
    path: '/error/:errorType/:p?',
    name: 'errorPage',
    component: ErrorPage,
  },
  {
    path: `/${SITE_MAINTENANCE_URL}`,
    name: SITE_MAINTENANCE_URL,
    component: SiteMaintenance,
  },
  {
    path: '/reference-guide/:coi?/:p?',
    name: 'referenceGuide',
    component: ReferenceGuide,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
  {
    path: '/claim-sandbox',
    name: 'claimSandbox',
    component: ClaimSandbox,
    meta: {
      auth: true,
    },
    beforeEnter: checkPermissions,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// VueGtag - Google Analytics - Automatic Router tracking
trackRouter(router)

router.beforeEach((to, from, next) => {
  // Global Maintenance Block (note the return statements) of the Application
  if (
    (window._MAINTENANCE && window._MAINTENANCE === true) ||
    __MAINTENANCE === 'true'
  ) {
    if (to.name !== SITE_MAINTENANCE_URL)
      return next({ name: SITE_MAINTENANCE_URL })
    else return next() // allow to proceed to Site Maintenance
  } else if (to.name === SITE_MAINTENANCE_URL) return next('/')

  const authenticated = store.state.user.isAuthenticated

  // Route Not Found case - bad link or user tampering with routes
  if (to.matched.length === 0) {
    // LOG BAD URLs explicitly
    if (authenticated) {
      $.post(`${API_URL}/ui/log`, {
        eventType: 'UIInfo',
        eventDescription: `Page Not Found -- User Navigation to "${to.fullPath}" from "${from.fullPath}"`,
        applicationRoute: to.fullPath,
        applicationVersion: __APP_VERSION,
      })
    }

    next('/error/page-not-found')
    return
  }

  const authRequired = to.matched.some((route) => route.meta.auth)

  // If Page requires authentication and not authenticated, call GET USER
  if (authRequired && !authenticated) {
    // store deeplink in sessionStorage
    if (to.path !== '/') {
      window.sessionStorage.setItem(DEEPLINK_SESSION_STORAGE_KEY, to.fullPath)
    }

    // Try to call into the ESP API and get user info thus checking
    // whether we are really authenticated i.e. token cookie exists
    $.ajax({
      url: `${API_URL}/user`,
      method: 'GET',
      async: false,
    })
      .done(function (response) {
        // If the Response is 200 OK, we enter the site and store the user information (json)
        store.dispatch(`user/${ENTER_SITE}`, response)

        // Force exit to Site Maintenance (solves HARD refresh F5)
        window._MAINTENANCE = response.maintenanceMode
        if (window._MAINTENANCE) {
          next({ name: SITE_MAINTENANCE_URL })
          return
        }

        // Get all advisory messages
        $.get(`${API_URL}/advisory-messages`).done((response) => {
          store.commit(`user/${SET_ADV_MSGS}`, response)
        })

        // Proceed to either deeplink or 'to' destination
        let _to = window.sessionStorage.getItem(DEEPLINK_SESSION_STORAGE_KEY)
        if (_to) {
          window.sessionStorage.removeItem(DEEPLINK_SESSION_STORAGE_KEY)
          if (_to !== to.fullPath) {
            // next(_to) is causing an error for calling next more than once
            // forcing the redirect with traditional router push call
            router.push(_to)
            return
          } else {
            next()
          }
        } else {
          next()
        }
      })
      .fail(function (jqXHR) {
        if (
          jqXHR.status === 0 ||
          jqXHR.status.toString().search(/5[0-9][0-9]/) >= 0
        ) {
          next('/error/internal-server-error')
        }
      })
  } else if (authRequired && authenticated) {
    // this will allow for time to read the cookie that was updated by token extension
    setTimeout(() => {
      // Check for Extra Details about a Claim and include in Logging
      let claimRelatedInfo
      if (store.getters['claim/isClaimInProgress']) {
        claimRelatedInfo = `Market: ${store.state.claim.currentClaim.market.countryName}; ESP System ID: ${store.getters['claim/espSystemId']}; ESP Model ID: ${store.getters['claim/espModelId']}; `

        claimRelatedInfo += store.getters['claim/isCurrentClaimBulk']
          ? `Submission Type: BULK`
          : `Submission Type: SINGLE`

        if (store.getters['claim/singleEventDetails']) {
          claimRelatedInfo += `; Event ID: ${store.getters['claim/singleEventDetails'].incomeEvent.eventId}; Security: ${store.getters['claim/singleEventDetails'].incomeEvent.security}; CUSIP: ${store.getters['claim/singleEventDetails'].incomeEvent.cusip}; DR Record Date: ${store.getters['claim/singleEventDetails'].incomeEvent.adrRecordDate}; Procedure Type: ${store.getters['claim/singleEventDetails'].procedure.display}`
        }

        if (store.state.claim.currentClaim.upload) {
          claimRelatedInfo += `; Upload Info: ${JSON.stringify(
            store.state.claim.currentClaim.upload,
          )}`
        }
      }

      // LOG UI NAVIGATION
      $.post(`${API_URL}/ui/log`, {
        eventType: 'UIInfo',
        eventDescription: `User Navigation from "${from.fullPath}" to "${
          to.fullPath
        }"${claimRelatedInfo ? ` >>> New Claim <<<  ${claimRelatedInfo}` : ''}`,
        applicationRoute: to.fullPath,
        applicationVersion: __APP_VERSION,
      })

      // GET USER DATA ON EACH NAVIGATION
      $.get(`${API_URL}/user`)
        .done(function (response) {
          // If the Response is 200 OK, we enter the site and store the user information (json)
          store.commit(`user/${SET_USER_DATA}`, response)

          // Force exit to Site Maintenance (solves SOFT navigation using SPA routing)
          window._MAINTENANCE =
            response.maintenanceMode || __MAINTENANCE === 'true'
          if (window._MAINTENANCE) {
            return next({ name: SITE_MAINTENANCE_URL })
          } else next()
        })
        .fail(function (jqXHR) {
          if (
            jqXHR.status === 0 ||
            jqXHR.status.toString().search(/5[0-9][0-9]/) >= 0
          ) {
            next('/error/internal-server-error')
          }
        })

      $.get(`${API_URL}/advisory-messages`).done((response) => {
        store.commit(`user/${SET_ADV_MSGS}`, response)
      })
    }, 500)
  } else {
    // Authenticated or No Authentication needed - proceed to route
    next()
  }
})

export default router
